import { browsePage, publisherPage } from '@exchange-frontends/utils';

import { detailsPage, featurePage, featurePageEventNameConversion, managePage } from './launchConstants';

/**
 *
 * @typedef {Object} SiteSearch
 * @property {Array<Filter>} filter - used to determine which items will be included in the search results
 * @property {Array<Sort>} sort - used to determine type and order of sorting
 * @property {number} numberOfResults - number of search results
 * @property {string} query - the terms searched for
 * @property {?Array<string>} suggestions - search type
 */

/**
 * @typedef {Object} Filter
 * @property {string} attribute - filter attribute
 * @property {Array<string>} value - filter value
 */

/**
 * @typedef {Object} Sort
 * @property {string} attribute - sort attribute
 * @property {string} order - the order of sorting
 **/

/**
 * Record analytics for license dialogue in acquire button
 * @param {string} targetElementTextValue - target element text value
 */
export const handleLicenseDialogueForLaunch = (targetElementTextValue) => {
  if (targetElementTextValue) {
    const formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForHeader}:${targetElementTextValue}:click`;
    recordAnalytics(formattedEventName);
  }
};

/**
 * Record analytics for app details page
 * @param {Event} event - event object
 */
export const handleAppDetailsForLaunch = (event) => {
  const pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;
  let formattedEventName;

  if (pageSection === 'MORE BY THIS PUBLISHER') {
    formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForMoreByThisPublisher}:${targetElementTextValue}:click`;
  } else if (pageSection === 'Tags' || pageSection === 'Publisher' || pageSection === 'Categories') {
    formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForTagsAndPublisher}:${pageSection}:${targetElementTextValue}:click`;
  } else if (detailsPage.rightNavHelpAndSupportItems.includes(targetElementTextValue)) {
    formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForHelpAndSupport}:${targetElementTextValue}:click`;
  } else if (pageSection == 'next-item' || pageSection == 'previous-item') {
    formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForCarousel}:${pageSection}:click`;
  }

  recordAnalytics(formattedEventName);
};

/**
 * Record analytics for home page
 * @param {Event} event - event object
 */
export const handleHomeForLaunch = (event) => {
  let pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;

  for (const key of Object.keys(featurePageEventNameConversion)) {
    //we need to use combination of data-launchid and innertext, because there are two viewall in feature page
    if (targetElementTextValue === key) {
      pageSection = featurePageEventNameConversion[key];
    }
    if (pageSection === key) {
      pageSection = featurePageEventNameConversion[key];
    }
  }

  // Code is repeated because you can click on any of the page section and it will have target values and page section value will be present if there is only launchid associated with it
  /* we are not using string.includes here because clicking on page randomly might contain the target element text */
  if (
    targetElementTextValue === featurePage.productName1 ||
    targetElementTextValue === featurePage.productName2 ||
    targetElementTextValue === featurePage.productName3
  ) {
    const formattedEventName = `${featurePage.pageName}:${pageSection}:${targetElementTextValue}:click`;
    recordAnalytics(formattedEventName);
  }
};

/**
 * Record analytics for profile page
 * @param {MouseEvent<HTMLDivElement>} event - event object
 */
export const handleProfileForLaunch = (event) => {
  const pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;

  if (pageSection === 'By this Publisher') {
    const formattedEventName = `${publisherPage.pageName}:${pageSection}:${targetElementTextValue}:click`;
    recordAnalytics(formattedEventName);
  }
};

/**
 * Record analytics for header
 * @param {Event} event - event object
 */
export const handleHeaderForLaunch = (event) => {
  const pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;

  if (pageSection === 'free' || pageSection === 'paid' || pageSection === 'trial' || pageSection === 'Manage') {
    const formattedEventName = `${detailsPage.pageName}:${detailsPage.sectionHeadingForHeader}:${targetElementTextValue}:click`;
    recordAnalytics(formattedEventName);
  }
};

/**
 * Record analytics for browse page
 * @param {Event} event - event object
 * @param {Object} searchInfo - search information
 */
export const handleBrowseForLaunch = (event, searchInfo) => {
  const pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;
  sendDataToLaunch(pageSection, targetElementTextValue, searchInfo);
};

//
/**
 * Record analytics from elements where event is not captured
 * @param {string} pageSection - page section
 * @param {string} targetElementTextValue - target element text value
 * @param {Object} searchInfo - search information
 */
export const sendDataToLaunch = (pageSection, targetElementTextValue, searchInfo) => {
  if (pageSection) {
    let formattedEventName;
    if (pageSection === 'next-item' || pageSection === 'previous-item') {
      formattedEventName = `${browsePage.pageName}:${browsePage.sectionHeadingForFeaturedCarousel}:${pageSection}:click`;
    } else if (pageSection === 'Search Results') {
      formattedEventName = `${browsePage.pageName}:${pageSection}:${targetElementTextValue}:click`;
    } else if (pageSection === 'featured') {
      formattedEventName = `${browsePage.pageName}:${browsePage.sectionHeadingForFeaturedCard}:${targetElementTextValue}:click`;
    } else if (pageSection === 'filters') {
      formattedEventName = `${browsePage.pageName}:${targetElementTextValue}:click`;
    }
    const siteSearch = processSearchInfo(searchInfo);
    recordAnalytics(formattedEventName, siteSearch);
  }
};

/**
 * Record analytics for manage page
 * @param {string} formattedEventName - formatted event name based on landing page
 **/
export const handleManageForLaunch = (event) => {
  const pageSection = event?.target?.dataset?.launchid;
  const targetElementTextValue = event?.target?.innerText;

  let formattedEventName;

  if (
    pageSection === 'Apps pending review' ||
    pageSection === 'Approved apps' ||
    pageSection === 'Manage CC plugins' ||
    pageSection === 'Experience Cloud Applications'
  ) {
    formattedEventName = `${managePage.pageName}:${pageSection}:click`;
  } else if (pageSection === 'Review') {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingPendingReview}:${targetElementTextValue}:click`;
  } else if (pageSection === 'Revoke') {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingApprovedApps}:${targetElementTextValue}:click`;
  } else if (
    pageSection === 'Cancel reviewing app' ||
    pageSection === 'Reject the app' ||
    pageSection === 'Approve the app'
  ) {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingReviewApp}:${targetElementTextValue}:click`;
  } else if (pageSection === 'Cancel app revoke' || pageSection === 'Revoke app') {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingRevokeApp}:${targetElementTextValue}:click`;
  } else if (pageSection === 'Manage button') {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingManageApps}:${targetElementTextValue}:click`;
  } else {
    formattedEventName = `${managePage.pageName}:${managePage.SectionHeadingManageApps}:manage`;
  }

  const siteSearch = processSearchInfo(getDefaultManageSearchInfo());
  recordAnalytics(formattedEventName, siteSearch);
};

/**
 * Get default manage search information needed for analytics
 * @returns {Object} - default manage search information
 */
export const getDefaultManageSearchInfo = () => {
  const org = document.getElementById('manage-org')?.innerText;
  const sort = document.getElementById('manage-sort')?.innerText;
  return { org, sort };
};

/**
 * Process search information to be sent to analytics event/state
 * @param {Object} searchInfo - search information
 * @param {?string|number} totalSearchResult - total search results
 * @returns {SiteSearch}
 */
export const processSearchInfo = (searchInfo, totalSearchResult) => {
  /** @type {SiteSearch} */
  const siteSearch = {
    filter: [],
    sort: [],
    numberOfResults: parseInt(totalSearchResult) || 0,
  };

  if (!searchInfo) {
    return siteSearch;
  }

  Object.keys(searchInfo).forEach((key) => {
    const value = searchInfo[key];
    if (!value) {
      return;
    }
    if (key === 'q') {
      siteSearch.query = value;
    } else if (key === 'sort') {
      siteSearch.sort = [
        {
          attribute: value,
          order: 'asc',
        },
      ];
    } else {
      siteSearch.filter.push({
        attribute: key,
        value: [].concat(value),
      });
    }
  });

  return siteSearch;
};

/**
 * Record analytics event when clicking on an element
 * @param {string} formattedEventName - formatted event name based on landing page
 * @param {SiteSearch} siteSearch - site search for capturing search information
 */
const recordAnalytics = (formattedEventName, siteSearch) => {
  window?._satellite?.track('event', {
    xdm: {},
    data: {
      web: {
        webInteraction: {
          name: formattedEventName,
        },
      },
      _adobe_corpnew: {
        // Any custom properties required for event
        digitalData: {
          primaryEvent: {
            eventInfo: {
              eventName: formattedEventName,
            },
          },
        },
      },
      siteSearch,
    },
  });
};
